import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { get } from 'lodash';
import getColour from '@lendi-ui/commons/colours';
import Grid from '@lendi-ui/grid';
import { Heading } from '@lendi-ui/typography';

import { FIELD_PATHS, FREQUENCY, LIABILITY_TYPE, URL_PARAM_APPLICATION_ID } from '@funnels/constants';
import { UseParams } from '@funnels/types';
import { BoldHeading, LinkButton, StringDisplay, SummaryStringDisplay } from '@funnels/ui-components';
import { formatNumberToStringPrecision, getFieldValueLabel } from '@funnels/utils';

import { GridMargin, SummarySection } from '../../_common/style';
import { LiabilityWithAnnualRepayment } from './types';

interface SummaryProps {
  liability: LiabilityWithAnnualRepayment;
  owner: string;
}

const ENTITY_TO_URL_SLUG: { [key: string]: string } = {
  [LIABILITY_TYPE.PERSONAL_LOAN]: 'loans',
  [LIABILITY_TYPE.CREDIT_CARD]: 'credit-card',
  [LIABILITY_TYPE.STORE_CARD]: 'credit-card',
};

export default function LiabilitySummary({ liability, owner }: SummaryProps) {
  const history = useHistory();
  const { [URL_PARAM_APPLICATION_ID]: applicationId } = useParams<UseParams>();
  const type = get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_TYPE(0));
  const financialInstitution = get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(0));
  const repaymentAmount = get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(0));
  const repaymentFrequency = get(
    { liabilities: [liability] },
    FIELD_PATHS.LIABILITY_REPAYMENT_FREQUENCY(0),
    FREQUENCY.MONTHLY
  );
  const annualRepayment = liability.annualRepayment;
  const entity = ENTITY_TO_URL_SLUG[type] || 'additional';
  const title = getFieldValueLabel(type);

  function handleEditClick() {
    history.push(`/application/${applicationId}/liabilities/${entity}/?from=summary`);
  }

  return (
    <SummarySection data-testid="SummarySection">
      <GridMargin mb={'sm'} halign="justify" valign="center">
        <BoldHeading size="sm">{title}</BoldHeading>
        <LinkButton color={getColour('interaction', 'main')} text={'EDIT'} onClick={handleEditClick} />
      </GridMargin>
      <GridMargin mb={'md'}>
        <Grid.Unit size={10 / 23}>
          <SummaryStringDisplay title={'Institution'} value={financialInstitution} />
        </Grid.Unit>
        <Grid.Unit offset={3 / 23} size={10 / 23}>
          <SummaryStringDisplay title={'Owner'} value={owner} shouldMaskValue />
        </Grid.Unit>
      </GridMargin>
      {repaymentAmount && repaymentFrequency && (
        <GridMargin mb={'lg'}>
          <Grid.Unit size={10 / 23}>
            <SummaryStringDisplay title={'Repayment amount'} value={`$${repaymentAmount}`} />
          </Grid.Unit>
          <Grid.Unit offset={3 / 23} size={10 / 23}>
            <SummaryStringDisplay title={'Occurrence'} value={repaymentFrequency} />
          </Grid.Unit>
        </GridMargin>
      )}
      {annualRepayment > 0 && (
        <GridMargin mb={'lg'}>
          <Grid.Unit size={10 / 23}>
            <Heading size="xs">Total Repayments</Heading>
          </Grid.Unit>
          <Grid.Unit offset={3 / 23} size={10 / 23}>
            <strong>
              <StringDisplay
                value={`$${formatNumberToStringPrecision(annualRepayment, 2)}/yr`}
                textColor={getColour('text', 'main')}
                textSize="md"
              />
            </strong>
          </Grid.Unit>
        </GridMargin>
      )}
    </SummarySection>
  );
}
